import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCollections, useDocument, useDocuments, useMoveDocument } from "~/api/queries";
import { ABILITY, type Document } from "~/api/types";
import { Select } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Text } from "~/foundation/components/Text";

/**
 * Everything under `documentId`, from a flat list. The server does NOT reject a cycle —
 * `Document.SetParent` takes the id and stores it — so a document offered as its own descendant's
 * parent would detach a whole subtree from the tree with no error. Computed here rather than
 * hoped for.
 */
function descendantIds(documents: Document[], rootId: string): Set<string> {
  const byParent = new Map<string, Document[]>();
  for (const document of documents) {
    const parent = document.parentDocumentId;
    if (!parent) {
      continue;
    }
    byParent.set(parent, [...(byParent.get(parent) ?? []), document]);
  }
  const out = new Set<string>([rootId]);
  const queue = [rootId];
  while (queue.length > 0) {
    const next = queue.shift();
    if (next === undefined) {
      break;
    }
    for (const child of byParent.get(next) ?? []) {
      if (!out.has(child.id)) {
        out.add(child.id);
        queue.push(child.id);
      }
    }
  }
  return out;
}

/**
 * W10 §1 — move a document to another collection and/or under another parent.
 *
 * The endpoint applies the two destinations INDEPENDENTLY and treats an omitted field as "leave
 * it alone", so this dialog can only ever move a document somewhere — never out of a collection
 * and never (once nested) back to a collection's root. The parent select says so instead of
 * offering a "Top level" option that would silently do nothing.
 */
export function MoveDocumentDialog({
  documentId,
  onClose,
}: {
  documentId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: document } = useDocument(documentId);
  const { data: collections } = useCollections();
  const move = useMoveDocument();

  const [collectionId, setCollectionId] = useState("");
  const [parentId, setParentId] = useState("");

  // Where the parent options come from: the destination if one is chosen, else where it is now.
  const scope = collectionId || document?.collectionId || "";
  const { data: siblings } = useDocuments(scope ? { collectionId: scope, limit: 100 } : undefined);

  const writable = (collections ?? []).filter(
    (collection) =>
      collection.abilities[ABILITY.collectionUpdate] === true ||
      collection.abilities[ABILITY.collectionCreateDocument] === true,
  );

  const forbidden = useMemo(
    () => descendantIds(siblings ?? [], documentId),
    [siblings, documentId],
  );
  const parents = (siblings ?? []).filter((candidate) => !forbidden.has(candidate.id));

  const changed = collectionId !== "" || parentId !== "";

  const submit = () => {
    if (!changed) {
      return;
    }
    move.mutate(
      {
        id: documentId,
        ...(collectionId ? { collectionId } : {}),
        ...(parentId ? { parentDocumentId: parentId } : {}),
      },
      {
        onSuccess: (moved) => {
          onClose();
          toast.success(t("Moved “{{title}}”", { title: moved.title }));
        },
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Move “{{title}}”", { title: document?.title ?? "" })}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={!changed || move.isPending} onClick={submit}>
            {move.isPending ? t("Moving…") : t("Move")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Select
          fullWidth
          label={t("Collection")}
          value={collectionId}
          onChange={(event) => {
            setCollectionId(event.target.value);
            // A parent from the old collection cannot survive a change of collection.
            setParentId("");
          }}
        >
          <option value="">{t("Keep the current collection")}</option>
          {writable.map((collection) => (
            <option key={collection.id} value={collection.id}>
              {collection.emoji ? `${collection.emoji} ` : ""}
              {collection.name}
            </option>
          ))}
        </Select>

        <Select
          fullWidth
          label={t("Nest under")}
          value={parentId}
          disabled={parents.length === 0}
          onChange={(event) => setParentId(event.target.value)}
        >
          <option value="">{t("Keep the current position")}</option>
          {parents.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.title}
            </option>
          ))}
        </Select>

        <Text variant="small">
          {t(
            "A document can be moved into a collection or nested under another document — the platform has no way to move it back out to a collection's root.",
          )}
        </Text>
      </div>
    </Dialog>
  );
}
